import { dateOnly, formatMonthDayDow } from '@indipia/shared-features';

// 받은 발주 목록 (외부·무인증 공유 화면, share/SUPPLIER_ORDER_LIST) — 순수 로직·문구 상수 (시험 대상).
// 서버 계약: PLAN_E 2.3 `GET /order-share-lists/me?filter=WAIT|REPLIED|ALL`.

export type ListFilter = 'WAIT' | 'REPLIED' | 'ALL';
export type NextAction = 'REPLY' | 'DELIVERY' | 'VIEW';

export const LIST_PATH = '/share/SUPPLIER_ORDER_LIST';
export const REPLY_PATH = '/share/SUPPLIER_ORDER_REPLY';
export const DELIVERY_PATH = '/share/SUPPLIER_DELIVERY';

/** 건별 화면에 붙이는 목록 진입 표식(쿼리). 목록 토큰으로 건별 API 를 부를 때 서버가 이 값으로 (사업장, 거래처) 소속을 확인한다. */
export const ORDER_VENDOR_PARAM = 'orderVendorId';

export const FILTERS: readonly { code: ListFilter; label: string }[] = [
  { code: 'WAIT', label: '회신할 것' },
  { code: 'REPLIED', label: '회신한 것' },
  { code: 'ALL', label: '받은 것' },
];

export const TEXT = {
  documentTitle: '받은 발주',
  headerTitle: (companyName: string) => (companyName ? `${companyName} · 받은 발주` : '받은 발주'),
  headerSub: (vendorName: string, windowDays: number) => [vendorName, `최근 ${windowDays}일`].filter(Boolean).join(' · '),
  empty: (windowDays: number) => `최근 ${windowDays}일 동안 받은 발주가 없어요`,
  listFail: '목록을 불러오지 못했어요',
  linkReply: '회신하기 ›',
  linkDelivery: '배송정보 입력 ›',
  linkView: '내용 보기 ›',
  hintDelivery: (label: string) => `입고희망 ${label}`,
  backToList: '← 목록으로',
} as const;

// ── 서버 규격 ───────────────────────────────────────────────

export interface OrderListItem {
  orderVendorId: number;
  siteLabel: string;
  workTypeName: string;
  itemSummary: string;
  /** 'YYYY-MM-DD' 또는 빈 문자열(값이 없거나 형식 이상이면 표시 줄에서 생략) */
  deliveryDate: string;
  statusCode: string;
  nextAction: NextAction;
  sendExpired: boolean;
}

export interface OrderListView {
  companyName: string;
  vendorName: string;
  windowDays: number;
  counts: { wait: number; replied: number; all: number };
  items: OrderListItem[];
}

export type ParsedOrderList = { ok: true; view: OrderListView } | { ok: false };

const NEXT_ACTIONS = new Set<string>(['REPLY', 'DELIVERY', 'VIEW']);

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function count(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

function safeDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  try {
    return dateOnly(v);
  } catch {
    return '';
  }
}

/**
 * 목록 응답 검증. 필수 값(counts·windowDays·items 의 식별자·다음 동작)이 없거나 약속된 값 밖이면 폴백 없이 실패로 돌려준다.
 * 사업장명·거래처명·현장·공종·품목 요약·입고희망일은 표시용이라 비어 있으면 해당 줄만 생략한다.
 */
export function parseOrderList(raw: unknown): ParsedOrderList {
  if (!raw || typeof raw !== 'object') return { ok: false };
  const r = raw as Record<string, unknown>;
  const c = r.counts && typeof r.counts === 'object' ? (r.counts as Record<string, unknown>) : null;
  const wait = c ? count(c.wait) : null;
  const replied = c ? count(c.replied) : null;
  const all = c ? count(c.all) : null;
  if (wait === null || replied === null || all === null) return { ok: false };
  const windowDays = typeof r.windowDays === 'number' && r.windowDays > 0 ? r.windowDays : null;
  if (windowDays === null) return { ok: false };
  if (!Array.isArray(r.items)) return { ok: false };
  const items: OrderListItem[] = [];
  for (const it of r.items) {
    if (!it || typeof it !== 'object') return { ok: false };
    const i = it as Record<string, unknown>;
    if (typeof i.orderVendorId !== 'number' || !Number.isInteger(i.orderVendorId) || i.orderVendorId <= 0) return { ok: false };
    if (typeof i.nextAction !== 'string' || !NEXT_ACTIONS.has(i.nextAction)) return { ok: false };
    items.push({
      orderVendorId: i.orderVendorId,
      siteLabel: str(i.siteLabel),
      workTypeName: str(i.workTypeName),
      itemSummary: str(i.itemSummary),
      deliveryDate: safeDate(i.deliveryDate),
      statusCode: str(i.statusCode),
      nextAction: i.nextAction as NextAction,
      sendExpired: i.sendExpired === true,
    });
  }
  return {
    ok: true,
    view: { companyName: str(r.companyName), vendorName: str(r.vendorName), windowDays, counts: { wait, replied, all }, items },
  };
}

// ── 표시 ────────────────────────────────────────────────────

export function filterCount(counts: OrderListView['counts'], filter: ListFilter): number {
  return filter === 'WAIT' ? counts.wait : filter === 'REPLIED' ? counts.replied : counts.all;
}

/** 회신할 것은 입고희망일 빠른 순(날짜 없는 행은 뒤, 같으면 서버 순서 유지). 그 외는 서버 순서(최근 발송 순)를 그대로 쓴다. */
export function sortForDisplay(filter: ListFilter, items: readonly OrderListItem[]): OrderListItem[] {
  if (filter !== 'WAIT') return [...items];
  return items
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => {
      const da = a.it.deliveryDate;
      const db = b.it.deliveryDate;
      if (da && db && da !== db) return da < db ? -1 : 1;
      if (!da && db) return 1;
      if (da && !db) return -1;
      return a.idx - b.idx;
    })
    .map((x) => x.it);
}

/** 행 제목 `{현장} · {공종}` — 비어 있는 쪽은 생략 */
export function itemTitle(item: Pick<OrderListItem, 'siteLabel' | 'workTypeName'>): string {
  return [item.siteLabel, item.workTypeName].filter(Boolean).join(' · ');
}

/** 행 보조 `{품목 요약} · 입고희망 M/D(요일)` — 비어 있는 쪽은 생략 */
export function itemHint(item: Pick<OrderListItem, 'itemSummary' | 'deliveryDate'>): string {
  return [item.itemSummary, item.deliveryDate ? TEXT.hintDelivery(formatMonthDayDow(item.deliveryDate)) : ''].filter(Boolean).join(' · ');
}

/** 상태별 연결 문구 1개 */
export function linkText(action: NextAction): string {
  return action === 'REPLY' ? TEXT.linkReply : action === 'DELIVERY' ? TEXT.linkDelivery : TEXT.linkView;
}

export type BadgeTone = 'warning' | 'success' | 'danger' | 'mute';

// 거래처 화면용 상태 라벨. 기획 표기: 회신 대기=warning, 가능=success, 불가=danger, 입고 완료=mute. 배송정보 단계는 기획에 색 지정 없음 → 대기 warning·보냄 success.
const STATUS_BADGE: Record<string, { label: string; tone: BadgeTone }> = {
  REPLY_WAIT: { label: '회신 대기', tone: 'warning' },
  REREPLY_WAIT: { label: '재회신 대기', tone: 'warning' },
  CONFIRMED: { label: '가능', tone: 'success' },
  DECLINED: { label: '불가', tone: 'danger' },
  SHIP_WAIT: { label: '배송정보 대기', tone: 'warning' },
  SHIP_REPLIED: { label: '배송정보 등록', tone: 'success' },
  RECEIVED: { label: '입고 완료', tone: 'mute' },
};

/** 모르는 상태 코드는 null(배지 없음) — 다른 라벨로 대체하지 않는다. */
export function statusBadge(statusCode: string): { label: string; tone: BadgeTone } | null {
  return STATUS_BADGE[statusCode] ?? null;
}

/** 행 선택 = 상태별 단일 목적지: 회신 대기→회신 화면, 배송정보 대기→배송정보 화면, 그 외→읽기 전용 회신 화면. */
export function itemTarget(item: Pick<OrderListItem, 'orderVendorId' | 'nextAction'>): string {
  const base = item.nextAction === 'DELIVERY' ? DELIVERY_PATH : REPLY_PATH;
  return `${base}?${ORDER_VENDOR_PARAM}=${item.orderVendorId}`;
}

/** 쿼리 문자열에서 목록 진입 표식을 읽는다. 양의 정수가 아니면 null(건별 토큰 방식). */
export function parseOrderVendorId(search: string): number | null {
  const v = new URLSearchParams(search).get(ORDER_VENDOR_PARAM);
  if (v === null || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}
